import { ButtonDestructive, ButtonNavigationBack, ButtonPrimary } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { SidePanelProgressBar } from "@nova/component-side-panel-tab-container"
import { TimeElapsed } from "@nova/component-time-elapsed"
import {
    getSatelliteJobErrorMessage,
    JobStatusType,
    JobTypes,
    SATELLITE_JOB_TYPE_BY_SENSOR_TYPE,
    SatelliteSensorType,
} from "@nova/constants"
import { dateFromNumber } from "@nova/serializer/src/client/util"
import Image from "next/image"
import { selectSatelliteFlightById } from "features/entity-caching/flights/selectors"
import { deleteSatelliteFlight } from "features/pubsub/entityActions/flights/satellite"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    selectActiveLayerId,
    setActiveFeatureId,
    setActiveLayerId,
} from "panels/content/side-panel/slice.new"
import { useJobProgress } from "panels/content/side-panel/useJobProgress"
import { usePluginActionsContext } from "panels/content/side-panel/plugin-actions/usePluginActionsContext"
import { SatelliteMetadataRows } from "features/map/inspector-panel/satellite/SatelliteMetadataRows"
import { estimateSatelliteProcessingSeconds } from "./estimateProcessingDuration"

export const SatelliteProcessingPanel = () => {
    const dispatch = useAppDispatch()
    const { iconUrl } = usePluginActionsContext()
    const activeLayerId = useAppSelector(selectActiveLayerId)
    const flight = useAppSelector(selectSatelliteFlightById(activeLayerId ?? ""))
    const progress = useJobProgress({
        job: flight?.job,
        type:
            SATELLITE_JOB_TYPE_BY_SENSOR_TYPE[flight?.sensorType as SatelliteSensorType] ??
            JobTypes.Satellite,
        totalDurationSeconds: estimateSatelliteProcessingSeconds(
            flight?.region,
            flight?.sensorType as SatelliteSensorType
        ),
    })
    const jobFailed = flight?.job?.status === JobStatusType.FAILED

    const onBack = () => {
        dispatch(setActiveLayerId({ id: undefined }))
        dispatch(setActiveFeatureId({ feature: undefined }))
    }

    if (!flight) return null

    return (
        <div className="px-3 flex flex-col overflow-hidden h-full mb-2">
            <ButtonNavigationBack title="Back" onClick={onBack} className="self-start" />
            <div className="flex items-center mt-3 mb-4 gap-2 select-none">
                {iconUrl && (
                    <div className="w-6 h-6 shrink-0">
                        <Image
                            src={iconUrl}
                            alt=""
                            width={24}
                            height={24}
                            className="w-full h-full object-contain"
                            priority
                        />
                    </div>
                )}
                <h4 className="text-gray-0 overflow-hidden whitespace-nowrap overflow-ellipsis">
                    {flight.name}
                </h4>
            </div>

            <div className="border-b border-gray-700" />

            <div className="mt-4">
                {!jobFailed && (
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-1">
                                <h5 className="text-gray-0">Processing:</h5>
                                <h5 className="text-gray-100 mono">
                                    {Math.max(1, progress).toString()}%
                                </h5>
                            </div>
                            {flight.job?.createdAt && (
                                <TimeElapsed
                                    start={dateFromNumber(flight.job.createdAt)}
                                    className="!text-gray-100"
                                />
                            )}
                        </div>
                        <SidePanelProgressBar progress={progress} total={100} colorHex="#27BBFF" />
                    </div>
                )}

                {jobFailed && (
                    <div>
                        <div className="flex items-center mb-2 gap-1">
                            <Icons.SmallIcon Icon={Icons.Error} className="text-red-500" />
                            <h5 className="text-gray-0">Process Failed</h5>
                        </div>
                        <small className="text-gray-100">
                            {getSatelliteJobErrorMessage(flight.job?.error)}
                        </small>
                        <ButtonPrimary title="Done" className="mt-3 w-full" onClick={onBack} />
                    </div>
                )}
            </div>

            <div className="border-b border-gray-700 mt-4" />

            <div className="mt-2">
                <SatelliteMetadataRows flight={flight} />
            </div>

            <div className="border-b border-gray-700 mt-2" />

            <ButtonDestructive
                LeftIcon={Icons.Trash}
                isTransparent
                className="w-full mt-2 justify-center"
                onClick={() => {
                    dispatch(deleteSatelliteFlight({ id: flight.id }))
                    onBack()
                }}
            />
        </div>
    )
}
